/**
 * One stored field for each home fact.
 * Septic lives on sewer_type. House water can be city and well together.
 * One well pump year is shared when either house water or irrigation uses a well.
 * The agent invite form and Edit Property Details are the only two
 * places that collect the same facts. Both write these fields.
 * CommonJS only. No object spread.
 */

const { canonicalFeature, normalizeFeatureList } = require('./homeownerIntake');
const {
  ductedHvacCount,
  hvacUnitTotal,
  encodeHeaterType,
  hvacTypesField,
  hvacUnitSlots,
  oldestHeaterYear,
  parseHvacSystems,
  readHvacUnitYears,
} = require('./homeProfile');

const FILTER_INTERVALS = { '1in': 1, '4in': 6, washable: 3 };

const FILTER_TYPES = ['1in', '4in', 'washable', 'unknown'];

const WATER_SOURCE_CITY = 'City/Municipal Water';
const WATER_SOURCE_WELL = 'Well Water';

const HOUSE_WATER_SOURCES = [
  { id: 'city', label: 'City water', description: 'Indoor water from the utility.' },
  { id: 'well', label: 'Well', description: 'A private well. The pump year is saved once for the whole home.' },
];

const IRRIGATION_SOURCES = [
  { id: 'none', label: 'None', description: 'No sprinklers at this home.' },
  { id: 'city', label: 'City water', description: 'Potable water from the utility.' },
  { id: 'well', label: 'Well', description: 'Uses the same well and pump already saved for this home.' },
  { id: 'reclaimed', label: 'Reclaimed water', description: 'Utility water that is not for drinking.' },
  { id: 'lake', label: 'Lake, pond, or canal', description: 'Pumped from a lake, pond, or canal.' },
];
const SEWER_PUBLIC = 'Public Sewer';
const SEWER_SEPTIC = 'Septic System';

const FILTER_LABELS = {
  '1in': '1 inch filter',
  '4in': '4 inch filter',
  washable: 'Washable filter',
  unknown: 'Not sure yet',
};

const WATER_HEATER_CHOICES = [
  { id: 'Electric Tank', label: 'Tank, electric', kind: 'tank' },
  { id: 'Gas Tank', label: 'Tank, gas', kind: 'tank' },
  { id: 'Heat Pump Water Heater', label: 'Heat pump', kind: 'tank' },
  { id: 'Electric Tankless', label: 'Tankless, electric', kind: 'tankless' },
  { id: 'Gas Tankless', label: 'Tankless, gas', kind: 'tankless' },
  { id: 'Solar Water Heater', label: 'Solar', kind: 'solar' },
];

/**
 * Shown on the shared home form. Each one drives a task, a report line,
 * or a vendor match. Septic and well are not here. They come from sewer
 * and water.
 */
const VISIBLE_FEATURES = [
  { id: 'pool', label: 'Swimming pool' },
  { id: 'solar_panels', label: 'Solar panels' },
  { id: 'generator', label: 'Generator' },
  { id: 'hurricane_shutters', label: 'Storm shutters' },
  { id: 'impact_windows', label: 'Impact windows' },
  { id: 'gutters', label: 'Gutters' },
  { id: 'fence', label: 'Fence' },
  { id: 'garage', label: 'Garage' },
  { id: 'surge_protector', label: 'Whole-house surge protector' },
  { id: 'water_softener', label: 'Water softener' },
  { id: 'whole_house_filter', label: 'Whole-house water filter' },
];

/**
 * Removed from the forms. Existing values stay in the database until a
 * save rewrites features. They do not drive a task, a report, or a vendor match.
 */
const REMOVED_FIELDS = [
  { field: 'lot_size', reason: 'Nothing in tasks, the home report, or vendor matching reads lot size.' },
  { field: 'features.spa', reason: 'No task, report line, or vendor match.' },
  { field: 'features.fireplace', reason: 'No task, report line, or vendor match.' },
  { field: 'features.security_system', reason: 'No task, report line, or vendor match.' },
  { field: 'features.smart_home', reason: 'No task, report line, or vendor match.' },
  { field: 'features.outdoor_kitchen', reason: 'No task, report line, or vendor match.' },
  { field: 'features.ev_charger', reason: 'No task, report line, or vendor match.' },
  { field: 'features.screen_enclosure', reason: 'No task, report line, or vendor match.' },
  { field: 'features.septic', reason: 'The same fact is sewer_type. A second switch created two answers.' },
  { field: 'features.well', reason: 'The same fact is water_source. A second switch created two answers.' },
  { field: 'roof_type', reason: 'Roof material already stores this. The report still reads an old roof_type if material is empty.' },
  { field: 'property_photo URL box', reason: 'The photo picker writes property_photo_url. A pasted URL was a second way in.' },
  { field: 'inventory hvac, roof, water heater, well, septic, generator', reason: 'Those facts are on the home form. Inventory keeps appliances and pool equipment.' },
];

/**
 * Before → after. Agent invite and agent Edit Property Details both use the
 * after column. Homeowner setup and Property Details use that same column
 * once, not both.
 */
const FIELD_MAP = [
  { fact: 'Walls', before: 'Setup questions and Property Details', after: 'Home form (setup first, Property Details later). Agent invite and Edit Property Details share it.' },
  { fact: 'Outside finish, foundation, roof material, roof year', before: 'Setup questions and Property Details', after: 'Home form' },
  { fact: 'Air conditioner count', before: 'Setup, Property Details, and the systems editor', after: 'Home form' },
  { fact: 'Filter type', before: 'Maintenance plan and nowhere else', after: 'Home form, next to the air conditioners' },
  { fact: 'Filter size', before: 'Not stored per unit', after: 'Home form, one size for each unit (AC 1, AC 2, ...)' },
  { fact: 'Water', before: 'Feature chip Well and water_source', after: 'water_source, City and Well together. One pump year if irrigation uses the well too.' },
  { fact: 'Sewer', before: 'Feature chip Septic and sewer_type', after: 'sewer_type' },
  { fact: 'Water heater', before: 'Systems editor and inventory', after: 'Home form type and install year' },
  { fact: 'Storm shutters', before: 'Feature chip hurricane and hurricane_shutters', after: 'One chip, Storm shutters' },
  { fact: 'Generator', before: 'Asked in setup, missing from the homeowner feature list', after: 'One chip on the home form' },
  { fact: 'Year built and square feet', before: 'Systems editor and the agent form', after: 'Home form. The home report uses both.' },
  { fact: 'Lot size', before: 'Agent form and systems editor', after: 'Removed' },
  { fact: 'Home photo', before: 'Edit Property Details only, after the invite', after: 'Invite form and Edit Property Details, same property_photo_url' },
];

const RETIRED_FEATURES = [
  'spa', 'fireplace', 'security_system', 'smart_home', 'outdoor_kitchen',
  'ev_charger', 'screen_enclosure',
];

const INVENTORY_OWNED_BY_FACTS = ['hvac', 'roof', 'water_heater', 'well_pump', 'septic_system', 'generator'];

function alias(value) {
  return String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function asList(value) {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  return [value];
}

function listHas(value, needles) {
  const wanted = needles.map(alias);
  return asList(value).some((item) => wanted.indexOf(alias(item)) !== -1);
}

function hasWell(home) {
  return houseWaterIds(home).indexOf('well') !== -1;
}

function houseWaterIds(home) {
  const ids = [];
  asList(home && home.water_source).forEach((item) => {
    const key = alias(item).replace(/\//g, '_');
    if (key === 'city' || key === 'city_water' || key === 'city_municipal_water' || key === 'potable' || key === 'municipal') {
      if (ids.indexOf('city') === -1) ids.push('city');
    }
    if (key === 'well' || key === 'well_water') {
      if (ids.indexOf('well') === -1) ids.push('well');
    }
  });
  return ids;
}

function storedHouseWater(ids) {
  const out = [];
  if ((ids || []).indexOf('city') !== -1) out.push(WATER_SOURCE_CITY);
  if ((ids || []).indexOf('well') !== -1) out.push(WATER_SOURCE_WELL);
  return out;
}

function toggleHouseWater(current, id) {
  const list = houseWaterIds({ water_source: current });
  const at = list.indexOf(id);
  if (at === -1) list.push(id);
  else list.splice(at, 1);
  return storedHouseWater(list);
}

function usesSharedWell(home) {
  return hasWell(home) || irrigationUsesWell(home);
}

function irrigationId(value) {
  const key = alias(value);
  if (key === 'none' || key === 'no') return 'none';
  if (key === 'city' || key === 'city_water' || key === 'potable' || key === 'city_potable') return 'city';
  if (key === 'well' || key === 'well_water') return 'well';
  if (key === 'reclaimed' || key === 'reclaimed_water' || key === 'reuse') return 'reclaimed';
  if (key === 'lake' || key === 'pond' || key === 'canal' || key === 'lake_pond_canal') return 'lake';
  return '';
}

function irrigationSources(home) {
  const raw = home && (home.irrigation_source != null ? home.irrigation_source : home.irrigation_sources);
  const parts = Array.isArray(raw) ? raw : String(raw || '').split(/[|,]/);
  const ids = [];
  parts.forEach((part) => {
    const id = irrigationId(part);
    if (id && ids.indexOf(id) === -1) ids.push(id);
  });
  if (ids.indexOf('none') !== -1) return ['none'];
  return ids;
}

function irrigationUsesWell(home) {
  return irrigationSources(home).indexOf('well') !== -1;
}

function irrigationActive(home) {
  const list = irrigationSources(home);
  if (!list.length) return null;
  if (list.length === 1 && list[0] === 'none') return false;
  return true;
}

function toggleIrrigation(current, id) {
  if (id === 'none') {
    const on = irrigationSources({ irrigation_source: current }).indexOf('none') !== -1;
    return on ? [] : ['none'];
  }
  const list = irrigationSources({ irrigation_source: current }).filter((item) => item !== 'none');
  const at = list.indexOf(id);
  if (at === -1) list.push(id);
  else list.splice(at, 1);
  return list;
}

function hasSeptic(home) {
  return listHas(home && home.sewer_type, ['septic', 'septic_system', SEWER_SEPTIC]);
}

function waterHeaterKind(type) {
  const text = String(type || '').trim().toLowerCase();
  if (!text) return null;
  if (text.indexOf('tankless') !== -1) return 'tankless';
  if (text.indexOf('solar') !== -1) return 'solar';
  if (text.indexOf('tank') !== -1 || text.indexOf('heat pump') !== -1) return 'tank';
  return null;
}

function installAge(year, now) {
  const installed = Number(year);
  const current = now ? new Date(now).getFullYear() : new Date().getFullYear();
  if (!installed || installed < 1900 || installed > current + 1) return null;
  return current - installed;
}

function featuresFromFacts(features, home) {
  const normalized = normalizeFeatureList(features || []).features.filter((id) => (
    id !== 'septic' && id !== 'well' && RETIRED_FEATURES.indexOf(id) === -1
  ));
  const irrigation = irrigationActive(home);
  const kept = normalized.filter((id) => irrigation !== false || id !== 'irrigation');
  if (hasSeptic(home)) kept.push('septic');
  if (usesSharedWell(home)) kept.push('well');
  if (irrigation === true) kept.push('irrigation');
  const seen = {};
  return kept.filter((id) => {
    if (seen[id]) return false;
    seen[id] = true;
    return true;
  });
}

function singleChoice(value, city, other) {
  if (listHas(value, [other, alias(other)])) return other;
  if (listHas(value, [city, alias(city)])) return city;
  return '';
}

function readFilterSizes(home) {
  const raw = home && (home.hvac_filter_sizes || home.filter_sizes);
  const list = Array.isArray(raw) ? raw : [];
  return list.map((item) => (item == null ? '' : String(item).trim()));
}

function filterCadenceMonths(filterType) {
  const key = String(filterType || '').trim().toLowerCase();
  if (FILTER_INTERVALS[key]) return FILTER_INTERVALS[key];
  return 1;
}

function frequencyForMonths(months) {
  if (months === 6) return 'Semiannually';
  if (months === 3) return 'Quarterly';
  if (months === 12) return 'Annually';
  return 'Monthly';
}

function acFilterLines(home) {
  const listed = Array.isArray(home && home.hvac_types)
    ? home.hvac_types.length > 0
    : String((home && home.hvac_types) || '').trim() !== '';
  const raw = listed ? ductedHvacCount(home) : Number(home && home.hvac_count);
  const count = Math.max(listed ? 0 : 1, Math.min(8, raw || (listed ? 0 : 1)));
  const sizes = readFilterSizes(home);
  const lines = [];
  for (let index = 0; index < count; index += 1) {
    const size = sizes[index] ? ' · ' + sizes[index] : '';
    lines.push('AC ' + (index + 1) + size);
  }
  return lines;
}

function acFilterDescription(home) {
  const lines = acFilterLines(home);
  const cadence = FILTER_LABELS[String((home && home.filter_type) || '').toLowerCase()] || 'the filter';
  return 'Change ' + cadence.toLowerCase() + ' on every unit.\n\n' + lines.map((line, index) => (
    (index + 1) + '. ' + line + '. Turn that fan off, slide the filter in with the arrow toward the unit, and turn the fan back on.'
  )).join('\n');
}

function isAcFilterTask(task) {
  const key = task && task.conditions && task.conditions.key;
  if (key === 'fl_ac_filters') return true;
  const title = String((task && (task.title || task.task_name)) || '').toLowerCase();
  if (title === 'change ac filters') return true;
  if (!/filter/.test(title)) return false;
  if (/range hood|dryer|water|pool|refrigerator/.test(title)) return false;
  return /(\bhvac\b|\ba\/c\b|air condition|\bac\b)/.test(title);
}

function withFilterCadence(task, home) {
  if (!isAcFilterTask(task)) return task;
  const months = filterCadenceMonths(home && home.filter_type);
  return Object.assign({}, task, { frequency: frequencyForMonths(months) });
}

function homeFactsSignature(source) {
  const home = source || {};
  return [
    home.property_type || '',
    (featuresFromFacts(home.features, home) || []).slice().sort().join(','),
    home.construction_type || '',
    home.exterior_finish || '',
    home.foundation_type || '',
    home.roof_material || '',
    home.roof_year || home.roof_install_year || '',
    home.hvac_count || '',
    asList(home.hvac_install_years).join(','),
    home.filter_type || '',
    readFilterSizes(home).join(','),
    home.water_heater_type || '',
    home.water_heater_install_year || '',
    asList(home.water_source).join(','),
    irrigationSources(home).join(','),
    home.well_pump_install_year || '',
    asList(home.sewer_type).join(','),
    home.septic_install_year || '',
    home.year_built || '',
    home.square_feet || '',
  ].join('~');
}

function yearOrNull(value) {
  if (value == null || value === '') return null;
  const year = Number(value);
  if (!Number.isFinite(year)) return null;
  return Math.floor(year);
}

function storedFilterType(value) {
  const filter = String(value || '').toLowerCase();
  if (filter === '1in' || filter === '4in' || filter === 'washable') return filter;
  return null;
}

function isCanonicalAcFilterTask(task) {
  const key = task && task.conditions && task.conditions.key;
  if (key === 'fl_ac_filters') return true;
  return String((task && (task.title || task.task_name)) || '').trim().toLowerCase() === 'change ac filters';
}

function withoutLegacyFilterTasks(tasks) {
  const list = tasks || [];
  if (!list.some(isCanonicalAcFilterTask)) return list;
  return list.filter((task) => !isAcFilterTask(task) || isCanonicalAcFilterTask(task));
}

function heaterFlushTitle(task) {
  return String((task && (task.title || task.task_name)) || '').trim().toLowerCase();
}

function isCanonicalHeaterFlush(task) {
  const key = task && task.conditions && task.conditions.key;
  if (key === 'fl_wh_flush') return true;
  return heaterFlushTitle(task) === 'flush the tank water heater';
}

function isLegacyHeaterFlush(task) {
  if (isCanonicalHeaterFlush(task)) return false;
  return heaterFlushTitle(task) === 'drain and flush water heater';
}

function withoutLegacyHeaterFlush(tasks, catalog) {
  const source = catalog || tasks || [];
  if (!source.some(isCanonicalHeaterFlush)) return tasks || [];
  return (tasks || []).filter((task) => !isLegacyHeaterFlush(task));
}

function homeFactsPatch(source) {
  const home = source || {};
  const features = featuresFromFacts(home.features, home);
  const sizes = readFilterSizes(home).map((item) => item || null);
  const hasTypes = Array.isArray(home.hvac_types)
    ? home.hvac_types.length > 0
    : String(home.hvac_types || '').trim() !== '';
  const systems = parseHvacSystems(home.hvac_types, hasTypes ? null : home.hvac_count);
  const hvacField = hvacTypesField(systems);
  const hvacYears = readHvacUnitYears(Object.assign({}, home, { hvac_types: hvacField, hvac_count: null }));
  const heaters = encodeHeaterType(home.water_heater_type, home.water_heater_install_year);
  return {
    features,
    filter_type: storedFilterType(home.filter_type),
    hvac_filter_sizes: sizes,
    hvac_types: hvacField,
    hvac_count: hvacUnitTotal({ hvac_types: hvacField }) || null,
    hvac_install_years: hvacYears,
    water_heater_type: heaters || null,
    water_heater_install_year: oldestHeaterYear(heaters, null),
    water_source: storedHouseWater(houseWaterIds(home)),
    irrigation_source: irrigationSources(home),
    well_pump_install_year: usesSharedWell(home) ? yearOrNull(home.well_pump_install_year) : null,
    sewer_type: hasSeptic(home) ? [SEWER_SEPTIC] : (singleChoice(home.sewer_type, SEWER_PUBLIC, SEWER_SEPTIC) ? [SEWER_PUBLIC] : []),
    septic_install_year: hasSeptic(home) ? yearOrNull(home.septic_install_year) : null,
    year_built: yearOrNull(home.year_built),
    square_feet: home.square_feet == null || home.square_feet === '' ? null : Math.floor(Number(home.square_feet)) || null,
  };
}

const { stripTaskEmoji } = require('./taskEmoji');

function plainTaskTitle(title) {
  const text = stripTaskEmoji(title).trim();
  const match = /^inspect(?:\s+and)?\s+/i.exec(text);
  if (!match) return text;
  const rest = text.slice(match[0].length);
  const look = /^(the )?(roof|walls|wall|yard|brick|shingles|shingle|tiles|tile|stains|cracks|beams|mortar|exterior|grading|driveway|walkways|panels|shading|debris|wellhead|drain field|seals)/i.test(rest);
  if (/^inspect and /i.test(text)) return (look ? 'Look at and ' : 'Check and ') + rest;
  return (look ? 'Look at ' : 'Check ') + rest;
}

function scrubClaimWords(text) {
  return String(text || '')
    .replace(/\binspected\b/gi, 'checked')
    .replace(/\bverified\b/gi, 'checked')
    .replace(/\bcertified\b/gi, 'licensed')
    .replace(/\bapproved\b/gi, 'ready')
    .replace(/\bconfirmed\b/gi, 'noted');
}

function irrigationTaskMatches(conditions, home) {
  if (!conditions || !Array.isArray(conditions.irrigation) || !conditions.irrigation.length) return true;
  const have = irrigationSources(home);
  return conditions.irrigation.some((id) => have.indexOf(id) !== -1);
}

function featureSelectedVisible(features, id) {
  return (featuresFromFacts(features, {}) || []).indexOf(canonicalFeature(id) || id) !== -1
    || (Array.isArray(features) && features.indexOf(id) !== -1);
}

module.exports = {
  FILTER_TYPES,
  FILTER_LABELS,
  WATER_SOURCE_CITY,
  WATER_SOURCE_WELL,
  HOUSE_WATER_SOURCES,
  IRRIGATION_SOURCES,
  SEWER_PUBLIC,
  SEWER_SEPTIC,
  WATER_HEATER_CHOICES,
  VISIBLE_FEATURES,
  REMOVED_FIELDS,
  FIELD_MAP,
  RETIRED_FEATURES,
  INVENTORY_OWNED_BY_FACTS,
  hasWell,
  hasSeptic,
  houseWaterIds,
  toggleHouseWater,
  usesSharedWell,
  irrigationSources,
  irrigationUsesWell,
  irrigationActive,
  toggleIrrigation,
  irrigationTaskMatches,
  waterHeaterKind,
  installAge,
  featuresFromFacts,
  readFilterSizes,
  filterCadenceMonths,
  frequencyForMonths,
  acFilterLines,
  acFilterDescription,
  isAcFilterTask,
  isCanonicalAcFilterTask,
  withoutLegacyFilterTasks,
  withoutLegacyHeaterFlush,
  storedFilterType,
  withFilterCadence,
  homeFactsSignature,
  homeFactsPatch,
  plainTaskTitle,
  stripTaskEmoji,
  scrubClaimWords,
  featureSelectedVisible,
  singleChoice,
};
